/*
 * WaWiIcAuftragApp.tsx - Einen Auftrag anzeigen und bearbeiten.
 *
 * Created by Dr. Maximillian Dornseif 2021-11-02 in huWaWi3 23.3.0
 * Copyright (c) 2021-2023 HUDORA GmbH
 */

import { Adresse, Anlieferdatum, IAdresse, LadeChecker, useSchemaPanel } from '@hudora/hd-react-components'
import { ValueRender } from '@hudora/react-jsonschema-valuerender'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { TreeView } from '@mui/x-tree-view/TreeView'
import { useTitle } from 'fluentui-hooks'
import { DateTime } from 'luxon'
import * as React from 'react'
import { gql, useQuery } from 'urql'

import { HwAuftrag } from '../../gql/graphql'
import { KundenCard } from '../../lib/components/KundenCard'
import { useAuditPanel3 } from '../../lib/hooks/useAuditPanel3'
import { useRohdatenPanel3 } from '../../lib/hooks/useRohdatenPanel3'
import { LinkRender } from '../../lib/hd-link'
import { CommandBar, ICommandBarItemProps } from '../../lib/muipanels'
import { DisplayNiceH2Audit, useH2AuditPanel } from '../components/H2Audit'
import { statusToText } from './statusZuText'
import { useAuftragEditGui } from './useAuftragEditGui'
import { useBelegGui } from './useBelegGui'
import { useGenehmigenGui } from './useGenehmigenGui'
import { useSperrGui } from './useSperrGui'
import { useStornierenGui } from './useStornierenGui'
import { useVersandanweisungGui } from './useVersandanweisungGui'
import { useZuteilungGui } from './useZuteilungGui'
import { Belege } from './WaWiAuftragEinerBelege'
import { AuftragsPositionen } from './WaWiAuftragEinerPositionen'
import { AUFTRAGSZUSATZPOS_QUERY, AUFTRAG_QUERY } from './WaWiAuftragEinerQuery'
import { TtVorgaenge } from './WaWiAuftragEinerTT'
import { AuftragsWert } from './WaWiAuftragEinerWert'
import { ZusatzLeistungen } from './WaWiAuftragEinerZusatzleistungen'

export const EinAuftragApp = (props: { nr: string }) => {
  const [{ fetching: loadingAuftrag, error: errorAuftrag, data }] = useQuery({
    query: AUFTRAG_QUERY,
    variables: { nr: props.nr },
  })
  const auftrag: HwAuftrag = {
    ...(data?.entity || {}),
  }
  const [schemaItem, schemalPanel] = useSchemaPanel('HwAuftrag')
  const [rohdatenItem, rohdatenPanel] = useRohdatenPanel3({
    entity: data?.entity,
    schemaName: 'HwAuftrag',
  })
  const [auditItem2, auditPanel2] = useH2AuditPanel(props.nr)
  const [sperrItem] = useSperrGui(auftrag)
  const [genehmigenItem] = useGenehmigenGui(auftrag)
  const [belegItem] = useBelegGui(auftrag)
  const [stornierenItem] = useStornierenGui(auftrag)
  const {
    commandBarItem: versandanweisungItem,
    versandanweisungenShowPanel: versandanweisungComponent,
    versandanweisungenEditPanel: versandanweisungDialog,
  } = useVersandanweisungGui(auftrag)
  const [zuteilungItem, zuteilungDialog] = useZuteilungGui(auftrag)
  const [editItem, editDialog] = useAuftragEditGui(auftrag)
  const [auditItem, auditPanel] = useAuditPanel3({
    defaultName: 'Auftrag',
    designator: props.nr,
    entity: auftrag,
  })

  const nearBarItems: ICommandBarItemProps[] = [
    belegItem,
    genehmigenItem,
    sperrItem,
    zuteilungItem,
    stornierenItem,
    editItem,
    versandanweisungItem,
    // {
    //   key: 'beschleunigen',
    //   text: 'Beschleunigen',
    //   ariaLabel: 'Beschleunigen',
    //   iconOnly: false,
    //   iconProps: { iconName: 'FastForward' },
    //   buttonStyles: importantItemStyle,
    //   disabled: false,
    //   // onClick: () => openPanel(),
    // },
  ].filter(Boolean) as ICommandBarItemProps[]

  const farBarItems = [auditItem, auditItem2, rohdatenItem, schemaItem]

  useTitle(props.nr)
  const dtSx = { fontSize: '80%', color: 'text.secondary' }
  return (
    <>
      <Stack direction="row" spacing={2}>
        <h1>{props.nr}</h1>
        <div style={{ width: '100%' }}>
          <CommandBar items={nearBarItems} farItems={farBarItems} />
        </div>
      </Stack>
      {zuteilungDialog}
      {versandanweisungDialog}
      {editDialog}
      {auditPanel2}
      {auditPanel}
      {rohdatenPanel}
      {schemalPanel}
      <LadeChecker loading={loadingAuftrag} error={errorAuftrag} label={`Lade Auftrag ${props.nr}`}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <div>
              <dl>
                <dt>
                  <Typography sx={dtSx}>huWaWi2</Typography>
                </dt>
                <dd>
                  <a href={`https://huwawi.hudora.de/k/${auftrag?.empfaengernr}/o/${auftrag?.auftragsnr}/`}>
                    {`https://huwawi.hudora.de/k/${auftrag?.empfaengernr}/o/${auftrag?.auftragsnr}/`}
                  </a>
                </dd>
                <dt>
                  <Typography sx={dtSx}>Status</Typography>
                </dt>
                <dd>
                  <span title={auftrag?.status}>{statusToText(auftrag?.status)}</span>
                  {auftrag.immutable ? <span title="immutable">🔐</span> : null}
                </dd>
                {auftrag.status < '400zugeteilt' ? (
                  <>
                    <dt>Zuteilung</dt>
                    <dd>
                      {auftrag.zuteilung_gescheitert_at ? (
                        <span>
                          Zuletzt gescheitert:{' '}
                          <ValueRender value={auftrag.zuteilung_gescheitert_at} format="date-time" />
                        </span>
                      ) : null}
                      {/*
                        <span id="zuteilungsfaehig" class="label label-success"></span>
                      {% if zuteilungsinfo %}
                        letzter Versuch ({{ zuteilungsinfo.created_at.strftime('%H:%M') }}): {{ zuteilungsinfo.message }}
                      {% endif %} */}
                    </dd>
                  </>
                ) : null}
                {/* <dt>Kreditlimit</dt>
              <dd>??</dd> */}
                <dt>
                  <Typography sx={dtSx}>Kundenreferenz / -info</Typography>
                </dt>
                <dd>
                  {auftrag.auftragsnr_kunde} / {auftrag.infotext_kunde}
                </dd>
                <dt>
                  <Typography sx={dtSx}>Wert / geliefert / offen</Typography>
                </dt>
                <dd>
                  <AuftragsWert auftrag={auftrag} />
                </dd>
                {auftrag.infotext_intern ? (
                  <>
                    <dt>
                      <Typography sx={dtSx}>interne Info</Typography>
                    </dt>
                    <dd>{[auftrag.infotext_intern].filter((x) => !!(x && x.length)).join(',')}</dd>
                  </>
                ) : null}
                <dt>
                  <Typography sx={dtSx}>Anlieferdatum</Typography>
                </dt>
                <dd>
                  <Anlieferdatum value={auftrag as any} />
                </dd>
                {auftrag?.warenempfaenger?.land ? (
                  <>
                    <dt>
                      <Typography sx={dtSx}>Warenempfänger</Typography>
                    </dt>
                    <dd>
                      <Adresse value={auftrag?.warenempfaenger as IAdresse} />
                    </dd>
                  </>
                ) : null}
              </dl>
              {auftrag.teillieferung_gewuenscht ? (
                <Chip label="Teillieferung erwünscht." color="info" size="small" />
              ) : null}{' '}
              {auftrag.auftragsrabatt_prozent > 0 ? (
                <Chip
                  label={`Auftragsrabatt "${auftrag.auftragsrabatt_text}": ${auftrag.auftragsrabatt_prozent}%`}
                  color="info"
                  size="small"
                />
              ) : null}{' '}
              {auftrag.lager !== 'LG200' ? (
                <Chip
                  label={
                    <span>
                      Versand von <strong>{auftrag.lager}</strong>
                    </span>
                  }
                  color="info"
                  size="small"
                />
              ) : null}{' '}
              {auftrag.zuteilungssperre ? (
                <Chip label="Zuteilungssperre" size="small" color="warning" />
              ) : null}{' '}
              {auftrag.storniert ? (
                <Chip label="Storno" color="warning" size="small" variant="outlined" />
              ) : null}{' '}
              {auftrag.erledigt ? (
                <Chip label="erledigt" color="success" size="small" variant="outlined" />
              ) : null}{' '}
              {auftrag.anlieferdatum_bis < DateTime.now().toISO() && auftrag.status < '600' ? (
                <Chip label="spät" color="error" size="small" variant="outlined" />
              ) : null}{' '}
              {auftrag.immutable ? (
                <Chip label="🔐" color="warning" size="small" variant="outlined" title="immutable" />
              ) : null}
            </div>

            <Delayed>
              {' '}
              <div>
                {versandanweisungComponent}
                <TtVorgaenge auftrag={auftrag} nr={props.nr} />
              </div>
              <div>
                <KundenCard nr={auftrag.empfaengernr} />
                <Belege auftrag={auftrag} nr={props.nr} />
                {/* Empfänger

Bearbeitung
Erfasst von
p.oltmanns@hudora.de@auth.gaetk2.23.nu
2021-11-11 13:33.
Geändert 2021-11-11 14:45. */}
              </div>
            </Delayed>
          </Stack>
          <AuftragsPositionen auftrag={auftrag} nr={props.nr} />
          <ZusatzLeistungen auftrag={auftrag} nr={props.nr} gqlQuery={AUFTRAGSZUSATZPOS_QUERY} />
          <DisplayNiceH2Audit nr={props.nr} />
          <AuftragDebug auftrag={auftrag} />
        </Stack>
      </LadeChecker>
    </>
  )
}

function AuftragDebug(props: { auftrag: HwAuftrag }) {
  let counter = 0
  return (
    <div>
      <h2>Folgedokumente</h2>
      <TreeView
        aria-label="Auftrag"
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
      >
        {/* <TreeItem nodeId={`${counter++}`} label="Herkunft">
          <TreeItem nodeId={`${counter++}`} label="EDI" />
          <TreeItem nodeId={`${counter++}`} label="Entwurf" />
        </TreeItem> */}
        <AdBestaetigungen counter={counter} auftrag={props.auftrag} />
        {/* <TreeItem nodeId={`${counter++}`} label="Veränderungen" /> */}
        <TreeItem nodeId={`${counter++}`} label="Komissionierungen">
          <AdKommiAuftraege counter={counter} auftrag={props.auftrag} />
        </TreeItem>
      </TreeView>
    </div>
  )
}

function AdBestaetigungen({ auftrag, counter }: { auftrag: HwAuftrag; counter: number }) {
  // Wir suchen hier den passenden Bestäutigungen aus den Auftragsbelegen.
  const [{ fetching, error, data }] = useQuery({
    query: AUFTRAG_BELEGE,
    variables: { nr: auftrag.auftragsnr },
  })

  return (
    <LadeChecker
      loading={fetching}
      error={error}
      label={`Lade Bestätigungen zu ${auftrag.auftragsnr}`}
      empty={!data}
    >
      <TreeItem nodeId={`ab${counter++}`} label="Bestätigungen">
        <TreeItem nodeId={`ab${counter++}`} label="Bestellbestätigungen">
          {(data?.hwAuftrag?.belege || [])
            .filter((x) => x?.belegart === 'Bestellbestätigung')
            .map((b) => (
              <>
                Beleg <LinkRender value={b?.designator} /> {b.created_at}
              </>
            ))}
        </TreeItem>
        <TreeItem nodeId={`ab${counter++}`} label="Auftragsbestätigungen">
          {(data?.hwAuftrag?.belege || [])
            .filter((x) => x?.belegart === 'Auftragsbestätigungen')
            .map((b) => (
              <>
                Beleg <LinkRender value={b?.designator} /> {b.created_at}
              </>
            ))}
        </TreeItem>
        <TreeItem nodeId={`ab${counter++}`} label="Proformarechnungen">
          {(data?.hwAuftrag?.belege || [])
            .filter((x) => x?.belegart === 'Proformarechnung')
            .map((b) => (
              <>
                Beleg <LinkRender value={b?.designator} /> {b.created_at}
              </>
            ))}
        </TreeItem>
        <TreeItem nodeId={`ab${counter++}`} label="Zollrechnung">
          {(data?.hwAuftrag?.belege || [])
            .filter((x) => x?.belegart === 'Zollrechnung')
            .map((b) => (
              <>
                Beleg <LinkRender value={b?.designator} /> {b.created_at}
              </>
            ))}
        </TreeItem>
      </TreeItem>{' '}
    </LadeChecker>
  )
}

function AdKommiAuftraege({ counter, auftrag }: { counter: number; auftrag: HwAuftrag }) {
  const [{ fetching, error, data }] = useQuery({
    query: gql`
      query AdAuftragKommis($nr: ID!) {
        hwAuftrag(designator: $nr) {
          id
          designator
          kommiauftragList {
            id
            designator
            lager
            status
            rueckgemeldet_at
            created_at
          }
        }
      }
    `,
    variables: { nr: auftrag.auftragsnr },
  })

  return (
    <LadeChecker loading={fetching} error={error} label={`Lade Kommiaufträge zu ${auftrag.auftragsnr}`}>
      {data?.hwAuftrag?.kommiauftragList?.map((kommiauftrag: any) => (
        <TreeItem nodeId={`k${counter++}`} label={`Kommiauftrag ${kommiauftrag.designator}`}>
          Lagervorgang {kommiauftrag.designator}
          <AdKommiBeleg counter={counter} komminr={kommiauftrag.designator} auftrag={auftrag} />
        </TreeItem>
      ))}
    </LadeChecker>
  )
}

function AdKommiBeleg({
  counter,
  komminr,
  auftrag,
}: {
  counter: number
  komminr: string
  auftrag: HwAuftrag
}) {
  const [{ fetching, error, data }] = useQuery({
    query: gql`
      query AdAuftragKommiBeleg($nr: ID!) {
        beleg(designator: $nr) {
          id
          designator
          deliveryId
          belegart
          belegdatum
          created_at
        }
      }
    `,
    variables: { nr: komminr },
  })

  const beleg = data?.beleg
  return (
    <LadeChecker loading={fetching} error={error} label={`Lade Kommibelege zu ${komminr}`} empty={!data}>
      Lagervorgang {komminr}
      <TreeItem nodeId={`k${counter++}`} label={`${beleg?.belegart} ${beleg?.designator}`}>
        Beleg {beleg?.created_at}
        <AdVorabLieferschein auftrag={auftrag} counter={counter} komminr={komminr} />
        <TreeItem nodeId={`kb${counter++}`} label="Rückmeldeauftrag (TBD)">
          Lagervorgang XXX (TBD)
          <AdRueckmeldeBeleg auftrag={auftrag} counter={counter} komminr={komminr} />
        </TreeItem>
      </TreeItem>
    </LadeChecker>
  )
}

const AUFTRAG_BELEGE = gql`
  query AdAuftragBelege($nr: ID!) {
    hwAuftrag(designator: $nr) {
      id
      designator
      belege {
        id
        designator
        deliveryId
        belegart
        belegdatum
        created_at
        raw
      }
    }
  }
`
function AdVorabLieferschein({
  auftrag,
  counter,
  komminr,
}: {
  auftrag: HwAuftrag
  counter: number
  komminr: string
}) {
  // Wir suchen hier den passenden Vorab-Lieferschein aus den Auftragsbelegen.
  const [{ fetching, error, data }] = useQuery({
    query: AUFTRAG_BELEGE,
    variables: { nr: auftrag.auftragsnr },
  })
  let beleg
  for (const b of data?.hwAuftrag?.belege || []) {
    if (b?.belegart === 'Vorablieferschein') {
      const raw = JSON.parse(b?.raw || '{}')
      if (raw?.bezug?.kommiauftrag?.nr === komminr) {
        beleg = b
      }
    }
  }

  return (
    <LadeChecker
      loading={fetching}
      error={error}
      label={`Lade Vorablieferschein zu ${komminr}`}
      empty={!data}
    >
      {beleg ? (
        <TreeItem nodeId={`kbv${counter++}`} label={`${beleg?.belegart} ${beleg?.designator}`}>
          Beleg <LinkRender value={beleg?.designator} /> {beleg?.created_at}
        </TreeItem>
      ) : (
        <TreeItem nodeId={`kbv${counter++}`} label={'Kein Vorab-Lieferschein gefunden'} />
      )}
    </LadeChecker>
  )
}
function AdRueckmeldeBeleg({
  auftrag,
  counter,
  komminr,
}: {
  auftrag: HwAuftrag
  counter: number
  komminr: string
}) {
  // Wir suchen hier den passenden Vorab-Lieferschein aus den Auftragsbelegen.
  const [{ fetching, error, data }] = useQuery({
    query: AUFTRAG_BELEGE,
    variables: { nr: auftrag.auftragsnr },
  })
  let beleg
  for (const b of data?.hwAuftrag?.belege || []) {
    if (b?.belegart === 'Rückmeldung') {
      const raw = JSON.parse(b?.raw || '{}')
      if (raw?.bezug?.kommiauftrag?.nr === komminr) {
        beleg = b
      }
    }
  }

  return (
    <LadeChecker loading={fetching} error={error} label={`Lade Rückmeldebeleg zu ${komminr}`} empty={!data}>
      <TreeItem nodeId={`kbr${counter++}`} label={`Rückmeldebeleg ${beleg?.designator}`}>
        Beleg <LinkRender value={beleg?.designator} /> {beleg?.created_at}
        <AdLieferschein auftrag={auftrag} counter={counter} komminr={komminr} />
      </TreeItem>
    </LadeChecker>
  )
}
function AdLieferschein({
  auftrag,
  counter,
  komminr,
}: {
  auftrag: HwAuftrag
  counter: number
  komminr: string
}) {
  // Wir suchen hier den passenden Vorab-Lieferschein aus den Auftragsbelegen.
  const [{ fetching, error, data }] = useQuery({
    query: AUFTRAG_BELEGE,
    variables: { nr: auftrag.auftragsnr },
  })
  let beleg
  for (const b of data?.hwAuftrag?.belege || []) {
    if (b?.belegart === 'Lieferschein') {
      const raw = JSON.parse(b?.raw || '{}')
      if (raw?.bezug?.kommiauftrag?.nr === komminr) {
        beleg = b
      }
    }
  }

  return (
    <LadeChecker loading={fetching} error={error} label={`Lade Lieferschein zu ${komminr}`} empty={!data}>
      <TreeItem nodeId={`kbrl${counter++}`} label={`Lieferschein ${beleg?.designator}`}>
        Beleg <LinkRender value={beleg?.designator} /> {beleg?.created_at}
        <TreeItem nodeId={`kbrl${counter++}`} label="Rechnungsentwurf (TBD)" />
      </TreeItem>
    </LadeChecker>
  )
}

// from https://stackoverflow.com/a/61390352/49407
type Props = {
  children: React.ReactNode
  waitBeforeShow?: number
}

const Delayed = ({ children, waitBeforeShow = 50 }: Props) => {
  const [isShown, setIsShown] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsShown(true)
    }, waitBeforeShow)
    return () => clearTimeout(timer)
  }, [waitBeforeShow])

  return isShown ? children : null
}
